import { useState, FormEvent } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../services/api'
import { createLogger } from '../../utils/createLogger'

const log = createLogger('ProfileSettings')

interface ProfileSettingsProps {
  user: any
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user }) => {
  const { refreshUser, logout } = useAuth()

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [profileForm, setProfileForm] = useState({
    given_name: user?.given_name || '',
    surname: user?.surname || '',
    job_title: user?.job_title || ''
  })
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState(false)

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // Profile editing handlers
  const handleEditProfile = () => {
    setIsEditingProfile(true)
    setProfileError(null)
    setProfileSuccess(false)
  }

  const handleCancelEdit = () => {
    setIsEditingProfile(false)
    setProfileForm({
      given_name: user?.given_name || '',
      surname: user?.surname || '',
      job_title: user?.job_title || ''
    })
    setProfileError(null)
  }

  const handleUpdateProfile = async (e: FormEvent) => {
    e.preventDefault()
    setProfileError(null)
    setProfileSuccess(false)
    setProfileLoading(true)

    try {
      await api.auth.updateProfile({
        given_name: profileForm.given_name,
        surname: profileForm.surname,
        job_title: profileForm.job_title || undefined
      })

      await refreshUser()
      setProfileSuccess(true)
      setIsEditingProfile(false)

      setTimeout(() => setProfileSuccess(false), 3000)
      log.info('Profile updated successfully')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update profile'
      setProfileError(message)
      log.error('Failed to update profile', error instanceof Error ? error : undefined)
    } finally {
      setProfileLoading(false)
    }
  }

  // Password change handlers
  const handleChangePasswordClick = () => {
    setIsChangingPassword(true)
    setPasswordError(null)
    setPasswordSuccess(false)
  }

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false)
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    })
    setPasswordError(null)
  }

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(false)

    // Validate passwords match
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match')
      return
    }

    // Validate password strength
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    setPasswordLoading(true)

    try {
      await api.auth.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      })

      setPasswordSuccess(true)
      setIsChangingPassword(false)
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })

      setTimeout(() => setPasswordSuccess(false), 3000)
      log.info('Password changed successfully')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to change password'
      setPasswordError(message)
      log.error('Failed to change password', error instanceof Error ? error : undefined)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleLogout = async () => {
    if (confirm('Are you sure you want to sign out?')) {
      await logout()
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Profile Settings</h3>

        {/* User Information Display */}
        {!isEditingProfile && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Display Name</label>
              <p className="mt-1 text-gray-900 dark:text-white">
                {user?.display_name || `${user?.given_name || ''} ${user?.surname || ''}`.trim() || 'Not set'}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</label>
              <p className="mt-1 text-gray-900 dark:text-white">{user?.email || 'Not set'}</p>
            </div>

            {user?.job_title && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Job Title</label>
                <p className="mt-1 text-gray-900 dark:text-white">{user.job_title}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Role</label>
              <p className="mt-1 text-gray-900 dark:text-white">
                {user?.roles?.[0] || 'User'}
              </p>
            </div>
          </div>
        )}

        {/* Profile Edit Form */}
        {isEditingProfile && (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name *
              </label>
              <input
                type="text"
                required
                value={profileForm.given_name}
                onChange={(e) => setProfileForm({ ...profileForm, given_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                disabled={profileLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                required
                value={profileForm.surname}
                onChange={(e) => setProfileForm({ ...profileForm, surname: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                disabled={profileLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Job Title
              </label>
              <input
                type="text"
                value={profileForm.job_title}
                onChange={(e) => setProfileForm({ ...profileForm, job_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                disabled={profileLoading}
              />
            </div>

            {profileError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-400">
                {profileError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={profileLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={profileLoading}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {profileSuccess && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-400">
            Profile updated successfully
          </div>
        )}
      </div>

      {/* Edit Profile Button */}
      {!isEditingProfile && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Profile Actions</h4>
          <button
            onClick={handleEditProfile}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors"
          >
            Edit Profile
          </button>
        </div>
      )}

      {/* Password Change Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Security</h4>

        {!isChangingPassword && (
          <div className="space-y-3">
            <button
              onClick={handleChangePasswordClick}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded transition-colors"
            >
              Change Password
            </button>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Update your account password
            </div>
          </div>
        )}

        {isChangingPassword && (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password *
              </label>
              <input
                type="password"
                required
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                disabled={passwordLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password *
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                disabled={passwordLoading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm New Password *
              </label>
              <input
                type="password"
                required
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                disabled={passwordLoading}
              />
            </div>

            {passwordError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-400">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-400">
                Password changed successfully
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={passwordLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
              <button
                type="button"
                onClick={handleCancelPasswordChange}
                disabled={passwordLoading}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Session Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Session</h4>

        <div className="space-y-3">
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded transition-colors"
          >
            Sign Out
          </button>

          <div className="text-sm text-gray-600 dark:text-gray-400">
            Sign out from your account
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfileSettings
